import React, { useState, useEffect } from 'react';
import { Heart, ArrowUp, Github, Linkedin, Twitter, Mail } from 'lucide-react';

const Footer = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Responsive breakpoint detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const socialLinks = [
    { icon: Github, href: 'https://github.com/Nithinreddy000', label: 'GitHub' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/nithin-reddy1/', label: 'LinkedIn' },
  ];

  const quickLinks = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Projects', href: '#projects' },
    { label: 'Experience', href: '#experience' },
    { label: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.substring(1));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <footer style={styles.footer}>
        <div style={styles.container}>
          <div style={{
            ...styles.mainGrid,
            gridTemplateColumns: isMobile 
              ? '1fr' 
              : isTablet 
              ? 'repeat(2, 1fr)' 
              : 'repeat(3, 1fr)',
            gap: isMobile ? '2.5rem' : '2rem'
          }}>
            {/* Brand Section */}
            <div style={{
              ...styles.brandSection,
              gridColumn: isMobile ? '1' : isTablet ? '1 / -1' : '1 / 2',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              <div style={styles.brandContent}>
                <h3 style={styles.brandTitle}>
                  Nithin Reddy
                </h3>
                <p style={{
                  ...styles.brandDescription,
                  maxWidth: isMobile ? '100%' : '320px',
                  margin: isMobile ? '0 auto' : '0'
                }}>
                  Full Stack Developer passionate about creating exceptional digital experiences 
                  that make a difference in people's lives.
                </p>
              </div>
              
              {/* Social Links */}
              <div style={{
                ...styles.socialContainer,
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.socialLink}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.2)';
                        e.target.style.borderColor = 'rgba(150, 199, 185, 0.5)';
                        e.target.style.color = '#96C7B9';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.1)';
                        e.target.style.borderColor = 'rgba(150, 199, 185, 0.3)';
                        e.target.style.color = '#B8C6D1';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      <Icon size={isMobile ? 20 : 18} />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Quick Links */}
            <div style={{
              ...styles.linksSection,
              textAlign: isMobile ? 'center' : 'left'
            }}>
              <h4 style={styles.sectionTitle}>
                Quick Links
              </h4>
              <ul style={styles.linksList}>
                {quickLinks.map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      style={styles.linkButton}
                      onMouseOver={(e) => {
                        e.target.style.color = '#96C7B9';
                        e.target.style.transform = 'translateX(4px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.color = '#B8C6D1';
                        e.target.style.transform = 'translateX(0)';
                      }}
                      onTouchStart={(e) => {
                        e.target.style.color = '#96C7B9';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onTouchEnd={(e) => {
                        e.target.style.color = '#B8C6D1';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div style={{
              ...styles.contactSection,
              textAlign: isMobile ? 'center' : 'left'
            }}>
              <h4 style={styles.sectionTitle}>
                Get in Touch
              </h4>
              <div style={styles.contactGrid}>
                <div style={styles.contactItem}>
                  <p style={styles.contactLabel}>Email</p>
                  <a 
                    href="mailto:nithinreddy3630@gmail.com" 
                    style={styles.contactLink}
                    onMouseOver={(e) => e.target.style.color = '#7BB5A7'}
                    onMouseOut={(e) => e.target.style.color = '#96C7B9'}
                  >
                    nithinreddy3630@gmail.com
                  </a>
                </div>
                
                <div style={styles.contactItem}>
                  <p style={styles.contactLabel}>Phone</p>
                  <a 
                    href="tel:+916305680684" 
                    style={styles.contactLink}
                    onMouseOver={(e) => e.target.style.color = '#7BB5A7'}
                    onMouseOut={(e) => e.target.style.color = '#96C7B9'}
                  >
                    +91 6305680684
                  </a>
                </div>
                
                <div style={styles.contactItem}>
                  <p style={styles.contactLabel}>Location</p>
                  <span style={styles.contactValue}>
                    Hyderabad, Telangana, India
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div style={{
            ...styles.bottomBar,
            flexDirection: isMobile ? 'column' : 'row',
            textAlign: isMobile ? 'center' : 'left',
            gap: isMobile ? '1.5rem' : '1rem'
          }}>
            <div style={styles.copyright}>
              <span>© {new Date().getFullYear()} Nithin Reddy. Made with</span>
              <Heart size={16} style={{ color: '#ef4444', margin: '0 0.25rem' }} />
              <span>using React</span>
            </div>

            {/* Back to Top Button */}
            <button
              onClick={scrollToTop}
              style={{
                ...styles.backToTopButton,
                padding: isMobile ? '1rem 2rem' : '0.75rem 1.5rem',
                fontSize: isMobile ? '1rem' : '0.9rem'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.2)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.1)';
                e.target.style.transform = 'scale(1)';
              }}
              onTouchStart={(e) => {
                e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.2)';
                e.target.style.transform = 'scale(0.95)';
              }}
              onTouchEnd={(e) => {
                e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.1)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              <span>Back to Top</span>
              <ArrowUp size={isMobile ? 18 : 16} />
            </button>
          </div>
        </div>

        {/* Decorative Top Border */}
        <div style={styles.decorativeBorder} />
      </footer>
    </>
  );
};

const styles = {
  footer: {
    backgroundColor: '#2C3E50',
    borderTop: '1px solid rgba(150, 199, 185, 0.2)',
    position: 'relative',
    width: '100%',
    overflow: 'hidden'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: 'clamp(2rem, 5vw, 3rem) clamp(1rem, 4vw, 2rem)'
  },
  mainGrid: {
    display: 'grid',
    marginBottom: 'clamp(2rem, 4vw, 3rem)',
    alignItems: 'start'
  },
  brandSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  brandContent: {
    marginBottom: '1rem'
  },
  brandTitle: {
    fontFamily: 'Comfortaa, sans-serif',
    fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
    fontWeight: '600',
    background: 'linear-gradient(135deg, #96C7B9 0%, #7BB5A7 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '0.75rem',
    margin: '0 0 0.75rem 0'
  },
  brandDescription: {
    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
    color: '#B8C6D1',
    lineHeight: '1.6',
    margin: '0'
  },
  socialContainer: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  socialLink: {
    padding: 'clamp(0.75rem, 2vw, 1rem)',
    borderRadius: '12px',
    backgroundColor: 'rgba(150, 199, 185, 0.1)',
    border: '1px solid rgba(150, 199, 185, 0.3)',
    color: '#B8C6D1',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    minWidth: '48px',
    minHeight: '48px'
  },
  linksSection: {
    display: 'flex',
    flexDirection: 'column'
  },
  sectionTitle: {
    fontFamily: 'Comfortaa, sans-serif',
    fontSize: 'clamp(1.1rem, 3vw, 1.25rem)',
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: '1rem',
    margin: '0 0 1rem 0'
  },
  linksList: {
    listStyle: 'none',
    padding: '0',
    margin: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#B8C6D1',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
    padding: '0.5rem 0',
    textAlign: 'inherit',
    width: '100%',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'inherit'
  },
  contactSection: {
    display: 'flex',
    flexDirection: 'column'
  },
  contactGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  contactItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
  },
  contactLabel: {
    color: '#B8C6D1',
    fontSize: 'clamp(0.8rem, 2vw, 0.85rem)',
    margin: '0',
    fontWeight: '500'
  },
  contactLink: {
    color: '#96C7B9',
    textDecoration: 'none',
    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
    transition: 'color 0.3s ease',
    wordBreak: 'break-all'
  },
  contactValue: {
    color: '#FFFFFF',
    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
  },
  bottomBar: {
    borderTop: '1px solid rgba(150, 199, 185, 0.2)',
    paddingTop: 'clamp(1.5rem, 3vw, 2rem)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  copyright: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    color: '#B8C6D1',
    fontSize: 'clamp(0.85rem, 2vw, 0.9rem)',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  backToTopButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    backgroundColor: 'rgba(150, 199, 185, 0.1)',
    border: '1px solid rgba(150, 199, 185, 0.3)',
    borderRadius: '12px',
    color: '#96C7B9',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    minHeight: '48px',
    whiteSpace: 'nowrap'
  },
  decorativeBorder: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, #96C7B9 50%, transparent 100%)',
    opacity: '0.5'
  }
};

export default Footer;
